import { Moon, Sun } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";

/**
 * ThemeToggle — light/dark switcher.
 *
 * Two visual variants:
 *   - "icon"    → 40×40 glass pill (used in the desktop header next to
 *                 the language globe; the same size class as
 *                 LanguageSwitcher's `globe` variant).
 *   - "inline"  → full-width row with icon + label. Used inside the
 *                 mobile drawer between the language row and the auth
 *                 widget. Matches the visual language of the
 *                 surrounding drawer items.
 *
 * Behaviour:
 *   - Click flips between light and dark and persists the explicit
 *     preference (subsequent visits are sticky regardless of the OS
 *     theme). See `theme/ThemeContext.jsx` for the storage policy.
 *   - The icon shown previews the destination (e.g. while in light mode
 *     we show a moon — "click to go dark"). This is the standard
 *     pattern most users expect.
 */

const LABELS = {
  ar: {
    toLight: "الوضع الفاتح",
    toDark: "الوضع الداكن",
    aria: "تبديل الوضع الداكن",
    rowTitle: "المظهر",
  },
  en: {
    toLight: "Light mode",
    toDark: "Dark mode",
    aria: "Toggle dark mode",
    rowTitle: "Appearance",
  },
};

export default function ThemeToggle({
  variant = "icon",
  lang = "en",
  testId = "theme-toggle",
  onToggle,
}) {
  const { isDark, toggleTheme } = useTheme();
  const t = LABELS[lang] || LABELS.en;
  const Icon = isDark ? Sun : Moon;
  // Show the destination state in the label, since the click changes it.
  const destinationLabel = isDark ? t.toLight : t.toDark;

  const handleClick = () => {
    toggleTheme();
    if (typeof onToggle === "function") onToggle();
  };

  if (variant === "inline") {
    return (
      <div
        className="flex items-center justify-between mt-3 pt-4"
        style={{ borderTop: "1px solid var(--sas-line)" }}
        data-testid={`${testId}-row`}
      >
        <span className="text-[12px] text-[color:var(--sas-ink-2)]">
          {t.rowTitle}
        </span>
        <button
          type="button"
          onClick={handleClick}
          className="
            inline-flex items-center gap-2
            h-9 px-3 rounded-full
            text-[13px] font-medium
            text-[color:var(--sas-ink)]
            sas-glass
            hover:border-[color:var(--sas-accent)]
            transition-colors
          "
          aria-pressed={isDark}
          aria-label={t.aria}
          data-testid={testId}
        >
          <Icon size={15} aria-hidden />
          <span>{destinationLabel}</span>
        </button>
      </div>
    );
  }

  // Default — compact 40×40 icon button matched to the language globe.
  return (
    <button
      type="button"
      onClick={handleClick}
      className="
        inline-flex items-center justify-center
        w-10 h-10 rounded-full
        sas-glass
        text-[color:var(--sas-ink-2)]
        hover:text-[color:var(--sas-accent)]
        hover:border-[color:var(--sas-accent)]
        transition-colors
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
        focus-visible:ring-[color:var(--sas-accent)]
      "
      aria-pressed={isDark}
      aria-label={t.aria}
      title={destinationLabel}
      data-testid={testId}
    >
      <Icon size={17} aria-hidden />
    </button>
  );
}
